/**
 * Session 60.25 · HoursCheckInModal
 *
 * Lightweight, member-facing modal that records that a Métier member is
 * working from a participating Métier Hours café/location. Optional Work
 * Intent, optional "Share to Commons" (OFF by default).
 *
 * Contract:
 *   props.cafeId    — canonical hours_cafes.cafe_id
 *   props.cafeName  — display name for the header line
 *   props.onClose   — callback when modal is dismissed (any reason)
 *   props.onSuccess — optional callback fired with the new check-in payload
 *
 * The modal reuses the existing HoursSharePrompt visual language: same
 * cream card, gold eyebrow, pill-shaped primary CTA. No shadcn Dialog
 * dependency (matches the rest of Hours-page UX).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const WORK_INTENTS = [
  { key: "deep_focus",         label: "Deep Focus" },
  { key: "taking_calls",       label: "Taking Calls" },
  { key: "client_meeting",     label: "Client Meeting" },
  { key: "team_collaboration", label: "Team Collaboration" },
  { key: "networking",         label: "Networking" },
  { key: "casual_work",        label: "Casual Work" },
];

export default function HoursCheckInModal({ cafeId, cafeName, onClose, onSuccess }) {
  const [intent, setIntent] = useState(null);
  const [shareCommons, setShareCommons] = useState(false); // OFF by default (spec)
  const [commonsBody, setCommonsBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  async function submit() {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post("/hours/check-ins", {
        hours_location_id: cafeId,
        work_intent: intent,
        share_to_commons: shareCommons,
        commons_body: shareCommons ? commonsBody.trim() : null,
      });
      const data = r.data || {};
      if (data.already_checked_in_today) {
        toast.success(`You checked in at ${cafeName} today.`);
      } else if (data.check_in?.commons_shared) {
        toast.success(`Checked in and shared to Commons.`);
      } else {
        toast.success(`Checked in at ${cafeName}.`);
      }
      if (onSuccess) onSuccess(data);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not record check-in.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="hours-checkin-title"
      data-testid="hours-checkin-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(9,14,32,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, backdropFilter: "blur(4px)",
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div
        style={{
          background: "#FAF8F3",
          color: "#1B2340",
          maxWidth: 480,
          width: "100%",
          borderRadius: 18,
          padding: 28,
          boxShadow: "0 30px 80px rgba(9,14,32,0.4)",
          border: "1px solid rgba(184,145,58,0.25)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#B8913A", fontWeight: 600 }}>
          Métier Hours · Check in
        </div>
        <h2
          id="hours-checkin-title"
          data-testid="hours-checkin-title"
          style={{
            fontFamily: "'Playfair Display', 'Cormorant Garamond', Georgia, serif",
            fontWeight: 500, fontSize: 28, lineHeight: 1.15,
            margin: "6px 0 6px",
          }}
        >
          Working from <em>{cafeName}</em>?
        </h2>
        <p style={{ fontSize: 14, lineHeight: 1.55, margin: 0, color: "rgba(27,35,64,0.72)" }}>
          Record this in your Places I&apos;ve Worked. Sharing is optional and private by default.
        </p>

        {/* Work Intent chips (optional) */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(27,35,64,0.65)", fontWeight: 600, marginBottom: 10 }}>
            Work intent <span style={{ textTransform: "none", letterSpacing: "0.02em", opacity: 0.7 }}>· optional</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {WORK_INTENTS.map(w => {
              const active = intent === w.key;
              return (
                <button
                  key={w.key}
                  type="button"
                  onClick={() => setIntent(active ? null : w.key)}
                  data-testid={`hours-checkin-intent-${w.key}`}
                  aria-pressed={active}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 140ms ease",
                    border: active ? "1px solid #B8913A" : "1px solid rgba(27,35,64,0.18)",
                    background: active ? "rgba(184,145,58,0.14)" : "#FFFFFF",
                    color: active ? "#8B6A22" : "#1B2340",
                  }}
                >
                  {w.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Share to Commons (OFF by default) */}
        <div
          style={{
            marginTop: 22,
            padding: "14px 16px",
            borderRadius: 12,
            background: shareCommons ? "rgba(184,145,58,0.10)" : "rgba(27,35,64,0.04)",
            border: shareCommons ? "1px solid rgba(184,145,58,0.28)" : "1px solid rgba(27,35,64,0.10)",
            transition: "all 140ms ease",
          }}
        >
          <label
            style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}
            data-testid="hours-checkin-share-toggle"
          >
            <input
              type="checkbox"
              checked={shareCommons}
              onChange={(e) => setShareCommons(e.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18, accentColor: "#B8913A" }}
              data-testid="hours-checkin-share-input"
            />
            <span>
              <span style={{ display: "block", fontWeight: 600, fontSize: 14 }}>
                Share to Commons
              </span>
              <span style={{ display: "block", fontSize: 12.5, lineHeight: 1.5, color: "rgba(27,35,64,0.7)", marginTop: 2 }}>
                Post a note letting the network know where you&apos;re working today.
              </span>
            </span>
          </label>
          {shareCommons && (
            <textarea
              value={commonsBody}
              onChange={(e) => setCommonsBody(e.target.value.slice(0, 1000))}
              placeholder="Say something (optional). E.g., getting focus hours in before my afternoon meetings."
              data-testid="hours-checkin-commons-body"
              rows={3}
              style={{
                marginTop: 12,
                width: "100%",
                padding: 10,
                borderRadius: 8,
                border: "1px solid rgba(27,35,64,0.18)",
                fontSize: 13.5,
                fontFamily: "inherit",
                resize: "vertical",
                background: "#FFFFFF",
                color: "#1B2340",
                boxSizing: "border-box",
              }}
            />
          )}
        </div>

        {/* CTAs */}
        <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="hours-checkin-submit"
            style={{
              padding: "13px 18px",
              borderRadius: 999,
              border: "1px solid rgba(139,106,34,0.55)",
              background: "linear-gradient(135deg,#D6B559 0%,#B8913A 55%,#8B6A22 100%)",
              color: "#FFF8E4",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: submitting ? "wait" : "pointer",
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? "Checking in…" : "✓ Check in"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="hours-checkin-cancel"
            style={{
              padding: "11px 18px",
              borderRadius: 999,
              border: "1px solid rgba(27,35,64,0.18)",
              background: "transparent",
              color: "#1B2340",
              fontSize: 13,
              fontWeight: 500,
              cursor: submitting ? "wait" : "pointer",
            }}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
